import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CalendarIcon, ClockIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useDateFormatter } from '../hooks/useDateFormatter';

interface DateTimePickerInputProps {
  id?: string;
  name?: string;
  value: string; // datetime-local format: YYYY-MM-DDTHH:mm
  onChange: (value: string) => void;
  className?: string;
  error?: boolean;
  disabled?: boolean;
  placeholder?: string;
  min?: string;
  max?: string;
  showTimeSelect?: boolean; // Default true - set to false for date-only picker
  required?: boolean;
  /** Minute dropdown step (1–30; must divide 60 evenly). Default 1 → every minute 0–59. */
  minuteStep?: number;
}

function clampMinuteStep(raw: number | undefined): number {
  const s = Math.round(raw ?? 1);
  if (s < 1) return 1;
  if (s > 30) return 30;
  if (60 % s !== 0) return 1;
  return s;
}

/**
 * Custom DateTimePicker that respects user preferences for date/time format
 *
 * Displays dates in user's preferred format (DD/MM/YYYY, MM/DD/YYYY, etc.)
 * but stores values in datetime-local format (YYYY-MM-DDTHH:mm) for form submission
 */
const DateTimePickerInput: React.FC<DateTimePickerInputProps> = ({
  id,
  name,
  value,
  onChange,
  className = '',
  error = false,
  disabled = false,
  placeholder,
  min,
  max,
  showTimeSelect = true,
  required = false,
  minuteStep: minuteStepProp = 1,
}) => {
  const minuteStep = clampMinuteStep(minuteStepProp);
  const { preferences, getToday } = useDateFormatter();

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState({ hours: '12', minutes: '00' });
  const [openUpward, setOpenUpward] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 300 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse the value to get selected date and time
  // Parse value as string parts to avoid timezone conversion issues
  useEffect(() => {
    if (value) {
      // Parse datetime-local format: YYYY-MM-DDTHH:mm
      const [datePart, timePart] = value.split('T');
      if (datePart) {
        const [year, month] = datePart.split('-').map(Number);
        if (!isNaN(year) && !isNaN(month)) {
          setCurrentMonth(new Date(year, month - 1, 1));
        }
      }
      if (timePart) {
        const [hours, minutes] = timePart.split(':');
        if (hours && minutes) {
          setSelectedTime({
            hours: hours.padStart(2, '0'),
            minutes: minutes.padStart(2, '0'),
          });
        }
      }
    }
  }, [value]);

  // Close dropdown when clicking outside (works with portal)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);
      
      // Only close if click is outside both container and dropdown
      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };

    // Use click event to allow calendar interactions first
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  // Calculate dropdown position when opening (viewport-aware for portal)
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const updatePosition = () => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const dropdownWidth = 300;
        const gap = 4;
        const viewportMargin = 8;
        
        // Try to get actual height from rendered dropdown, fallback to estimate
        let dropdownHeight: number;
        if (dropdownRef.current) {
          dropdownHeight = dropdownRef.current.getBoundingClientRect().height;
        } else {
          dropdownHeight = showTimeSelect ? 480 : 380;
        }

        const spaceBelow = window.innerHeight - rect.bottom - viewportMargin;
        const spaceAbove = rect.top - viewportMargin;
        const minSpaceRequired = dropdownHeight + gap;

        // Determine preferred direction: prefer opening below
        let shouldOpenUpward = false;
        let top: number;

        if (spaceBelow >= minSpaceRequired) {
          // Enough space below - open downward
          shouldOpenUpward = false;
          top = rect.bottom + gap;
        } else if (spaceAbove >= minSpaceRequired) {
          // Not enough space below, but enough above - open upward
          shouldOpenUpward = true;
          top = rect.top - dropdownHeight - gap;
        } else {
          // Not enough space in either direction - choose the one with more space
          if (spaceAbove > spaceBelow) {
            // More space above - clamp to viewport top
            shouldOpenUpward = true;
            top = Math.max(viewportMargin, rect.top - dropdownHeight - gap);
          } else {
            // More space below - clamp to viewport bottom
            shouldOpenUpward = false;
            const maxTop = window.innerHeight - dropdownHeight - viewportMargin;
            top = Math.min(maxTop, rect.bottom + gap);
          }
        }

        // Ensure top never goes above viewport
        top = Math.max(viewportMargin, top);
        
        // Ensure bottom never goes below viewport
        const calculatedBottom = top + dropdownHeight;
        if (calculatedBottom > window.innerHeight - viewportMargin) {
          top = Math.max(viewportMargin, window.innerHeight - dropdownHeight - viewportMargin);
        }

        setOpenUpward(shouldOpenUpward);
        
        // Center horizontally or align to left edge if near right edge
        const left = rect.left + (rect.width / 2) - (dropdownWidth / 2);
        const adjustedLeft = Math.max(viewportMargin, Math.min(left, window.innerWidth - dropdownWidth - viewportMargin));

        setDropdownPosition({
          top,
          left: adjustedLeft,
          width: dropdownWidth
        });
      };

      // Initial position calculation
      requestAnimationFrame(() => {
        updatePosition();
        // Update again after render to use actual height
        setTimeout(updatePosition, 0);
      });

      // Update position on resize (but not scroll, per requirements)
      const handleResize = () => {
        if (isOpen) {
          updatePosition();
        }
      };

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, showTimeSelect]);

  // Get display value in user's preferred format
  // IMPORTANT: Parse datetime-local string directly without Date object to avoid timezone conversion
  const getDisplayValue = useCallback(() => {
    if (!value) return '';

    // Parse datetime-local format: YYYY-MM-DD or YYYY-MM-DDTHH:mm
    const [datePart, timePart] = value.split('T');
    if (!datePart) return '';

    const dateParts = datePart.split('-');
    if (dateParts.length !== 3) return '';

    const year = dateParts[0];
    const month = dateParts[1];
    const day = dateParts[2];

    // Format date according to user preference
    let formattedDate: string;
    switch (preferences.dateFormat) {
      case 'dd/MM/yyyy':
        formattedDate = `${day}/${month}/${year}`;
        break;
      case 'yyyy-MM-dd':
        formattedDate = `${year}-${month}-${day}`;
        break;
      case 'dd-MMM-yyyy': {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthIdx = parseInt(month, 10) - 1;
        formattedDate = `${day}-${monthNames[monthIdx]}-${year}`;
        break;
      }
      case 'MM/dd/yyyy':
      default:
        formattedDate = `${month}/${day}/${year}`;
        break;
    }

    // For date-only mode, return just the date
    if (!showTimeSelect || !timePart) {
      return formattedDate;
    }

    // For datetime mode, add time in user's preferred format
    const [hours, minutes] = timePart.split(':');
    if (!hours || !minutes) return formattedDate;

    let formattedTime: string;
    if (preferences.timeFormat === '12H') {
      const h = parseInt(hours, 10);
      const period = h >= 12 ? 'PM' : 'AM';
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      formattedTime = `${displayHour}:${minutes} ${period}`;
    } else {
      formattedTime = `${hours}:${minutes}`;
    }

    return `${formattedDate}, ${formattedTime}`;
  }, [value, showTimeSelect, preferences.dateFormat, preferences.timeFormat]);

  // Generate calendar days
  const generateCalendarDays = useCallback(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startingDay = firstDayOfMonth.getDay(); // 0 = Sunday
    const daysInMonth = lastDayOfMonth.getDate();

    const days: (Date | null)[] = [];

    // Add empty slots for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  }, [currentMonth]);

  // Handle date selection
  // Build datetime string directly from date parts to avoid timezone issues
  const handleDateSelect = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    // Format based on whether time is included
    const formattedValue = showTimeSelect
      ? `${year}-${month}-${day}T${selectedTime.hours}:${selectedTime.minutes}`
      : `${year}-${month}-${day}`;
    onChange(formattedValue);

    if (!showTimeSelect) {
      setIsOpen(false);
    }
  };

  // Handle time change
  // Parse current value as string parts to avoid timezone conversion
  const handleTimeChange = (type: 'hours' | 'minutes', newValue: string) => {
    const newTime = { ...selectedTime, [type]: newValue };
    setSelectedTime(newTime);

    if (value) {
      // Parse the date part from value string directly
      const datePart = value.split('T')[0]; // "YYYY-MM-DD"
      if (datePart) {
        const hours = type === 'hours' ? newValue.padStart(2, '0') : newTime.hours.padStart(2, '0');
        const minutes = type === 'minutes' ? newValue.padStart(2, '0') : newTime.minutes.padStart(2, '0');

        const formattedValue = `${datePart}T${hours}:${minutes}`;
        onChange(formattedValue);
      }
    }
  };

  // Format date to datetime-local format
  const formatToDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Format date to date-only format (YYYY-MM-DD)
  const formatToDateOnly = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Check if a date is selected
  // Parse value string directly to avoid timezone conversion
  const isSelected = (date: Date): boolean => {
    if (!value) return false;

    // Parse datetime-local format: YYYY-MM-DD or YYYY-MM-DDTHH:mm
    const datePart = value.split('T')[0];
    if (!datePart) return false;

    const [year, month, day] = datePart.split('-').map(Number);
    return (
      date.getDate() === day &&
      date.getMonth() === month - 1 && // month is 0-indexed in Date
      date.getFullYear() === year
    );
  };

  // Check if a date is today (using user's timezone from preferences)
  const isToday = (date: Date): boolean => {
    const today = getToday(); // YYYY-MM-DD in user's timezone
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return dateStr === today;
  };

  // Check if date is within min/max bounds
  // Extract just the date portion from min/max since they might be datetime strings (YYYY-MM-DDTHH:mm)
  const isDateDisabled = (date: Date): boolean => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    // Extract date portion only (handle both YYYY-MM-DD and YYYY-MM-DDTHH:mm formats)
    const minDate = min ? min.split('T')[0] : null;
    const maxDate = max ? max.split('T')[0] : null;

    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;
    return false;
  };

  // Generate hours options
  const hoursOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

  // Minute dropdown: step grid (e.g. 10 → 00,10,…,50) plus current value if off-grid (e.g. 23:59 end-of-day).
  const minutesOptions = useMemo(() => {
    const base = Array.from({ length: 60 / minuteStep }, (_, i) =>
      (i * minuteStep).toString().padStart(2, '0')
    );
    if (!value || !value.includes('T')) return base;
    const timePart = value.split('T')[1];
    const currentMin = timePart?.split(':')[1];
    if (currentMin && /^\d{2}$/.test(currentMin) && !base.includes(currentMin)) {
      return [...base, currentMin].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    }
    return base;
  }, [minuteStep, value]);

  // Format hour for display based on user preference
  const formatHourForDisplay = (hour: string): string => {
    if (preferences.timeFormat === '12H') {
      const h = parseInt(hour, 10);
      if (h === 0) return '12 AM';
      if (h < 12) return `${h} AM`;
      if (h === 12) return '12 PM';
      return `${h - 12} PM`;
    }
    return hour;
  };

  // Handle "Now" / "Today" button click
  const handleNowClick = () => {
    const now = new Date();
    let minutes = Math.round(now.getMinutes() / minuteStep) * minuteStep;
    if (minutes >= 60) minutes = 60 - minuteStep;
    now.setMinutes(minutes);
    now.setSeconds(0);
    now.setMilliseconds(0);

    const formattedValue = showTimeSelect ? formatToDateTimeLocal(now) : formatToDateOnly(now);
    onChange(formattedValue);
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedTime({
      hours: now.getHours().toString().padStart(2, '0'),
      minutes: now.getMinutes().toString().padStart(2, '0'),
    });

    // Close the picker for date-only mode after selecting today
    if (!showTimeSelect) {
      setIsOpen(false);
    }
  };

  // Handle clear
  const handleClear = () => {
    onChange('');
    setIsOpen(false);
  };

  // Get month/year display
  const getMonthYearDisplay = (): string => {
    return currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Day names
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div ref={containerRef} className="relative">
      {/* Input Display */}
      <div
        className={`relative flex items-center w-full px-3 py-1 border rounded text-sm h-8 cursor-pointer ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'
        } ${
          error ? 'border-red-300 focus-within:ring-red-400' : 'border-gray-300 focus-within:ring-blue-400'
        } focus-within:outline-none focus-within:ring-1 ${className}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name}
          value={getDisplayValue()}
          readOnly
          disabled={disabled}
          required={required}
          placeholder={placeholder || (showTimeSelect ? 'Select date and time' : 'Select date')}
          className="flex-1 outline-none bg-transparent cursor-pointer disabled:cursor-not-allowed text-gray-900 placeholder-gray-400"
        />
        <CalendarIcon className="h-4 w-4 text-gray-400 ml-2" />
      </div>

      {/* Hidden input for form submission */}
      <input type="hidden" name={name ? `${name}_value` : undefined} value={value} />

      {/* Dropdown Calendar - Rendered via Portal to avoid overflow clipping */}
      {isOpen && !disabled && typeof window !== 'undefined' && createPortal(
        <div
          ref={dropdownRef}
          data-calendar-portal="true"
          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg p-4"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
          onMouseDown={(e) => {
            // Prevent mousedown events from bubbling to document-level handlers
            // This prevents FilterDropdown from closing when clicking inside calendar
            e.stopPropagation();
          }}
          onClick={(e) => {
            // Prevent clicks inside dropdown from bubbling to document
            e.stopPropagation();
          }}
        >
          {/* Header with navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={goToPreviousMonth}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <span className="font-medium text-gray-900">{getMonthYearDisplay()}</span>
            <button
              type="button"
              onClick={goToNextMonth}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRightIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Day names header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map((day) => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {generateCalendarDays().map((date, index) => (
              <div key={index} className="aspect-square">
                {date ? (
                  <button
                    type="button"
                    onClick={() => handleDateSelect(date)}
                    disabled={isDateDisabled(date)}
                    className={`w-full h-full flex items-center justify-center text-sm rounded-full transition-colors ${
                      isSelected(date)
                        ? 'bg-blue-600 text-white'
                        : isToday(date)
                        ? 'bg-blue-100 text-blue-600 font-medium'
                        : isDateDisabled(date)
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {date.getDate()}
                  </button>
                ) : null}
              </div>
            ))}
          </div>

          {/* Time selector */}
          {showTimeSelect && (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <ClockIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Time</span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={selectedTime.hours}
                  onChange={(e) => handleTimeChange('hours', e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  {hoursOptions.map((hour) => (
                    <option key={hour} value={hour}>
                      {formatHourForDisplay(hour)}
                    </option>
                  ))}
                </select>
                <span className="text-gray-500">:</span>
                <select
                  value={selectedTime.minutes}
                  onChange={(e) => handleTimeChange('minutes', e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  {minutesOptions.map((minute) => (
                    <option key={minute} value={minute}>
                      {parseInt(minute, 10)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200">
            <button
              type="button"
              onClick={handleNowClick}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {showTimeSelect ? 'Now' : 'Today'}
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-700"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DateTimePickerInput;
